import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/framework", label: "Framework" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  // Home has a dark hero — keep nav transparent over dark hero, then white on scroll
  const isHome = location.pathname === "/";
  const useDarkBg = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        useDarkBg
          ? "bg-transparent"
          : "bg-white/90 backdrop-blur-md border-b border-neutral-200"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img
            src="/assets/onehill-icon.png"
            alt="OneHill"
            className="h-10 w-auto select-none"
            draggable="false"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className={`font-display font-black tracking-tight text-xl ${useDarkBg ? "text-white" : "text-[#1F1F1F]"}`}>
              <span className="text-[#99CC00]">One</span>Hill
            </span>
            <span className={`mt-1 text-[9px] font-bold tracking-[0.28em] ${useDarkBg ? "text-white/55" : "text-neutral-500"}`}>
              COMMERCE PVT. LTD.
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "text-[#99CC00]"
                    : useDarkBg ? "text-white/85 hover:text-[#99CC00]" : "text-neutral-700 hover:text-[#99CC00]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta-partner"
          className="hidden md:inline-flex items-center gap-2 bg-[#99CC00] text-[#1F1F1F] px-6 py-3 rounded-full text-sm font-black hover:bg-[#B5E000] transition-colors"
        >
          Partner With OneHill
          <ArrowRight className="w-4 h-4" />
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border ${useDarkBg ? "text-white border-white/30" : "text-[#1F1F1F] border-neutral-300"}`}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-white border-t border-neutral-200">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-semibold border-b border-neutral-100 ${
                    isActive ? "text-[#99CC00]" : "text-[#1F1F1F] hover:text-[#99CC00]"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#99CC00] text-[#1F1F1F] px-5 py-3 rounded-full text-sm font-black"
            >
              Partner With OneHill <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
